import React, { FC, useState, useContext, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";
import {
  useStripe,
  useElements,
  PaymentElement,
} from "@stripe/react-stripe-js";

import { routes } from "@app/routing";
import { PostBillAPIContext } from "@app/provider";
import { SpinnerView } from "@app/components";
import { ButtonNoShadow } from "@app/components/buttonNoShadow";
import { Notification } from "@app/components";
import acknowledgements from "@etc/acknowledgements.json";
import { logMsg } from "@app/utils/logMsg";

const messages = defineMessages({
  billingUpdatePaymentUpdateButton: {
    id: "billingUpdatePaymentUpdateButton",
    defaultMessage: "Update",
  },
  billingUpdatePaymentSubtitle: {
    id: "billingUpdatePaymentSubtitle",
    defaultMessage: "You can use credit, debit, FSA, or HSA card",
  },
  billingUpdatePaymentFailedTitle: {
    id: "billingUpdatePaymentFailedTitle",
    defaultMessage: "Unable to Save Payment Method",
  },
  billingUpdatePaymentFailedDescription: {
    id: "billingUpdatePaymentFailedDescription",
    defaultMessage:
      "Please check that the information entered is correct or try using a different card.",
  },
  billingUpdatePaymentAcknowledgements: {
    id: "billingUpdatePaymentAcknowledgements",
    defaultMessage:
      "You can review important information from Natera on our {termsOfUse} and {privacyPolicy}",
  },
  billingUpdatePaymentTermsOfService: {
    id: "billingUpdatePaymentTermsOfService",
    defaultMessage: "Terms of Service",
  },
  billingUpdatePaymentPrivacyPolicy: {
    id: "billingUpdatePaymentPrivacyPolicy",
    defaultMessage: "Privacy Policy",
  },
});

const BillingUpdatePaymentForm: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const stripe = useStripe();
  const elements = useElements();
  const { updatePaymentMethod } = useContext(PostBillAPIContext);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const { paymentPlanId } = location.state ?? {};

  const isStripeReady = stripe && elements;

  const handleUpdatePaymentMethod = async (setupIntentId: string) => {
    try {
      await updatePaymentMethod({
        paymentPlanId,
        setupIntentId,
      });
      navigate(routes.billingPaymentSuccess);
    } catch {
      logMsg(
        "error",
        "postbill.payment",
        "Error updating payment plan request"
      );
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePaymentRequest = async () => {
    if (!isStripeReady) return;

    setLoading(true);
    setError(false);

    try {
      const { setupIntent, error } = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
      });

      if (error) {
        setError(true);
      } else {
        await handleUpdatePaymentMethod(setupIntent.id);
      }
    } catch {
      logMsg("error", "postbill.payment", "Error updating payment request");
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!paymentPlanId) {
      navigate(routes.billingCenter);
    }
  }, [paymentPlanId]);

  const renderAcknowledgements = () => {
    return intl.formatMessage(messages.billingUpdatePaymentAcknowledgements, {
      termsOfUse: (
        <a
          href={acknowledgements.links.termsOfUse}
          key="termsOfUse"
          target="_blank"
          rel="noreferrer"
        >
          {intl.formatMessage(messages.billingUpdatePaymentTermsOfService)}
        </a>
      ),
      privacyPolicy: (
        <a
          href={acknowledgements.links.privacyPolicy}
          key="privacyPolicy"
          target="_blank"
          rel="noreferrer"
        >
          {intl.formatMessage(messages.billingUpdatePaymentPrivacyPolicy)}
        </a>
      ),
    });
  };

  return (
    <>
      <SpinnerView isLoading={loading} />
      <div className="billing-update-payment__payment-form">
        {error && (
          <Notification
            className="billing-update-payment__notification"
            titleElem={intl.formatMessage(
              messages.billingUpdatePaymentFailedTitle
            )}
            type="warning"
          >
            {intl.formatMessage(messages.billingUpdatePaymentFailedDescription)}
          </Notification>
        )}
        <div className="billing-update-payment__subtitle">
          {intl.formatMessage(messages.billingUpdatePaymentSubtitle)}
        </div>
        <PaymentElement />
        <p className="billing-update-payment__footer-terms">
          {renderAcknowledgements()}
        </p>
        <ButtonNoShadow
          raised
          className="billing-update-payment__button"
          onClick={handleUpdatePaymentRequest}
        >
          {intl.formatMessage(messages.billingUpdatePaymentUpdateButton)}
        </ButtonNoShadow>
      </div>
    </>
  );
};

export default BillingUpdatePaymentForm;
